import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { DataService } from '../../../core/service/base/data.service';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { B2CSubscriptionContact } from '../../model/b2c/b2c-subscription-contact';
import { B2CSubscriptionSuggestion } from '../../model/b2c/b2c-subscription-suggestion';
import { InvoiceDeliveryMethod } from '../../model/const/enum';

export interface IB2CContactToggleResult {
  leaseAgreementId: string;
  deliveryMethod: InvoiceDeliveryMethod;
}

@Injectable()
export class B2CSubscriptionContactService extends DataService<B2CSubscriptionContact> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, B2CSubscriptionContact, progressBar, 'b2c-subscriptions/contact');
  }

  /**
   * Gets all 'usable' B2C subscriptions for a contact.
   *
   * A usable subscription is one that is not 'Closed' and has at least one mapped Contact (i.e. not 'Unmapped').
   *
   * The returned SubscriptionContact model has its Subscription loaded, but not its Contact (since this endpoint is primarily used from the contact-form).
   * The nested Subscription only has its <see cref="B2CSubscriptionViewModel.Identity"/> loaded and nothing else (all its nested collections are empty).
   */
  public getForContact(contactId: string): Observable<B2CSubscriptionContact[]> {
    if (!contactId) return of([]);

    return this.client.get<B2CSubscriptionContact[]>(`${this.endpoint}/${contactId}`).pipe(
      map(results => results.map(B2CSubscriptionContact.Map)),
      catchError(error => {
        this.notification.showError(error);
        return [];
      })
    );
  }

  /**
   * Returns an array of IB2CContactToggleResult for all agreements that were modified by this operation
   */
  public disableForContact(contactId: string): Observable<IB2CContactToggleResult[]> {
    if (!contactId) return of([]);

    return this.client.patch<IB2CContactToggleResult[]>(`${this.endpoint}/${contactId}/disable`, null).pipe(
      map(results => results.map(result => Object.assign({}, result))),
      catchError(error => {
        this.notification.showError(error);
        return [];
      })
    );
  }

  /**
   * Returns an array of IB2CContactToggleResult for all agreements that were modified by this operation
   */
  public enableForContact(contactId: string): Observable<IB2CContactToggleResult[]> {
    if (!contactId) return of([]);

    return this.client.patch<string[]>(`${this.endpoint}/${contactId}/enable`, null).pipe(
      map(modifiedLeaseAgreementIds =>
        modifiedLeaseAgreementIds.map(id => ({ leaseAgreementId: id, deliveryMethod: InvoiceDeliveryMethod.EInvoiceB2C }))
      ),
      catchError(error => {
        this.notification.showError(error);
        return [];
      })
    );
  }

  public getSuggestions(contactSweIdentityNo: string, contactId: string): Observable<B2CSubscriptionSuggestion[]> {
    if (!contactId) return of([]);

    return this.client.get<B2CSubscriptionSuggestion[]>(`${this.endpoint}/${contactId}/suggest/${contactSweIdentityNo}`).pipe(
      map(results => results.map(B2CSubscriptionSuggestion.Map)),
      catchError(error => {
        this.notification.showError(error);
        return [];
      })
    );
  }
}
